import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'angular-toastify';
import { ApiParkingFinanceService } from '../../../../../../core/services/api/finance/api-parking-finance.service';
import { MapperParkingFinanceService } from '../../../../../../core/mappers/finance/mapper-parking-finance.service';
import {
  IssueFullTaxFromFinanceForm,
  IssueFullTaxInvoiceFromFinanceResult,
  ParkingFinanceListRow
} from '../../../../../../core/models/finance/parking-finance.model';
import { PssLoadingComponent } from '../../../../../../tools/template/pss/pss-loading/pss-loading.component';
import { PssConfirmModalComponent } from '../../../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';

@Component({
  selector: 'app-modal-issue-full-tax-from-finance',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, PssConfirmModalComponent],
  templateUrl: './modal-issue-full-tax-from-finance.component.html',
  styleUrl: './modal-issue-full-tax-from-finance.component.css'
})
export class ModalIssueFullTaxFromFinanceComponent implements OnInit {
  @Input() record!: ParkingFinanceListRow;
  @Input() siteId = 0;
  @Input() userId = 0;
  @Input() menuId = 0;

  @Output() close = new EventEmitter<void>();
  @Output() issued = new EventEmitter<IssueFullTaxInvoiceFromFinanceResult>();

  private readonly apiService = inject(ApiParkingFinanceService);
  private readonly mapper = inject(MapperParkingFinanceService);
  private readonly toast = inject(ToastService);

  formData!: IssueFullTaxFromFinanceForm;
  isSubmitting = false;
  isConfirmModalOpen = false;

  ngOnInit(): void {
    this.formData = this.mapper.mapIssueFullTaxFormFromRecord(this.record);
  }

  onClose(): void {
    this.close.emit();
  }

  /** Validate form then open confirmation popup before submitting */
  onSubmitRequest(): void {
    if (this.isSubmitting) return;

    if (!this.userId || !this.menuId || !this.siteId || !this.record?.id) {
      this.toast.error('ข้อมูลไม่ครบถ้วน กรุณารีเฟรชหน้าจอแล้วลองใหม่');
      return;
    }

    const validationError = this.validateForm();
    if (validationError) {
      this.toast.error(validationError);
      return;
    }

    this.isConfirmModalOpen = true;
  }

  closeConfirmModal(): void {
    this.isConfirmModalOpen = false;
  }

  onConfirmSubmit(): void {
    this.isConfirmModalOpen = false;
    this.submit();
  }

  private submit(): void {
    if (this.isSubmitting) return;

    this.isSubmitting = true;
    const payload = this.mapper.mapIssueFullTaxPayload(
      this.formData,
      this.userId,
      this.menuId,
      this.siteId,
      this.record.id
    );

    this.apiService.issueFullTaxInvoiceFromFinance(payload).subscribe({
      next: (res) => {
        const result = this.mapper.mapIssueFullTaxResponse(res);
        this.isSubmitting = false;

        if (result.isSuccess) {
          const receiptMsg = result.newReceiptId ? ` เลขใบเสร็จใหม่: ${result.newReceiptId}` : '';
          this.toast.success((result.message || 'บันทึกคำขอใบกำกับภาษีเรียบร้อย') + receiptMsg);
          this.issued.emit(result);
          return;
        }

        this.toast.error(result.message || 'บันทึกคำขอใบกำกับภาษีไม่สำเร็จ');
      },
      error: (err) => {
        this.isSubmitting = false;
        const message = err?.error?.message ?? err?.error?.Message;
        this.toast.error(message || 'เกิดข้อผิดพลาดในการบันทึกคำขอใบกำกับภาษี');
      }
    });
  }

  private validateForm(): string | null {
    if (!this.formData.customerName.trim()) {
      return 'กรุณากรอกชื่อลูกค้า / บริษัท';
    }
    if (!this.formData.taxId.trim()) {
      return 'กรุณากรอกเลขประจำตัวผู้เสียภาษี';
    }
    if (!this.formData.address.trim()) {
      return 'กรุณากรอกที่อยู่';
    }
    if (!this.formData.details.trim()) {
      return 'กรุณากรอกรายละเอียดในใบกำกับภาษี';
    }
    if (!this.formData.headquarters && !this.formData.branchNo.trim()) {
      return 'กรุณาระบุรหัสสาขา หรือเลือกสำนักงานใหญ่';
    }
    return null;
  }
}
